import RootApiService from '@/services/api/RootApiService';
import ApiService from '../ApiService';
import Player from '@/models/Player';
import { translate } from '@/i18n';

import { makeLogger } from '@/services/utils/logger';
import { normalizePlayerName, validatePlayerName } from '@/services/utils/globalTools';
import { LOGGING } from '@/config';
const DEBUG_LOG = false;
const log = makeLogger('PlayerService', DEBUG_LOG, LOGGING.NET_SERVICES_LOG);

class PlayerApiService extends ApiService {
  async cancelMembership(clubId, playerId) {
    const response = await RootApiService.api.post(`/clubs/${clubId}/players/${playerId}/cancel-membership`);
    return response.data.data;
  }
  async getEditAccess(clubId, playerId) {
    const response = await RootApiService.api.get(`/clubs/${clubId}/players/${playerId}/edit-access`);
    return response.data.data;
  }

  async joinClub(clubId, playerId) {
    const response = await RootApiService.api.post(`/clubs/${clubId}/players/${playerId}/join`);
    return response.data.data;
  }
  async getForRegistration(playerId, clubId, ratingType = null) {
    const response = await this.api.get('/', { params: {
      filters: { documentId: { $eq: playerId }, chess_clubs: { documentId: { $eq: clubId } } },
      populate: {
        chess_clubs: { fields: ['documentId'] },
        ratings: {
          fields: ['controlType', 'rating', 'games', 'peak', 'kFactor', 'provisional'],
          ...(ratingType ? { filters: { type: { $eq: ratingType } } } : {}),
        },
      },
    } });
    const entry = this.asArray(response.data?.data)[0];
    if (!entry) throw new Error('Saved player not found');
    return this.deserialize(entry);
  }

  async getPaymentProfile(clubId, playerId) {
    if (!clubId || !playerId) return { feeExempt: false, comment: '', subscription: null, subscriptions: [] };
    const response = await RootApiService.api.get(`/clubs/${clubId}/players/${playerId}/payment-profile`);
    return response.data?.data || { feeExempt: false, comment: '', subscription: null, subscriptions: [] };
  }

  async savePaymentProfile(clubId, playerId, data) {
    const response = await RootApiService.api.put(`/clubs/${clubId}/players/${playerId}/payment-profile`, { data });
    return response.data?.data || null;
  }

  async createSubscription(clubId, playerId, data) {
    const { seasonId, ...values } = data;
    const response = await RootApiService.api.post('/player-subscriptions', {
      data: {
        ...values,
        player: playerId,
        club: clubId,
        season: seasonId || null,
      },
    });
    return response.data?.data || null;
  }

  async updateSubscription(subscriptionId, data) {
    const { seasonId, ...values } = data;
    const response = await RootApiService.api.put(`/player-subscriptions/${subscriptionId}`, {
      data: { ...values, season: seasonId || null },
    });
    return response.data?.data || null;
  }

  async deleteSubscription(subscriptionId) {
    const response = await RootApiService.api.delete(`/player-subscriptions/${subscriptionId}`);
    return response.data?.data || null;
  }

  async getSubscriptionDetails(subscriptionId) {
    const response = await RootApiService.api.get(`/player-subscriptions/${subscriptionId}/details`);
    return response.data?.data || { subscription: null, deductions: [], usedTournamentIds: [] };
  }

  async addSubscriptionDeduction(subscriptionId, tournamentId) {
    const response = await RootApiService.api.post('/subscription-deductions', {
      data: { subscription: subscriptionId, tournament: tournamentId },
    });
    return response.data?.data || null;
  }

  async removeSubscriptionDeduction(deductionId) {
    await RootApiService.api.delete(`/subscription-deductions/${deductionId}`);
  }

  normalizePlayerMark(value, kind = null) {
    const definition = (kind && value?.[kind]) || value?.mark || value || {};
    return {
      ...definition,
      assignmentId: definition === value ? null : value?.id || value?.documentId || null,
      documentId: definition.documentId || definition.id || null,
      kind,
      name: definition.name || '',
      shortName: definition.shortName || '',
      color: definition.color || '#6c757d',
      description: definition.description || '',
      visibility: definition.visibility || null,
      sortOrder: definition.sortOrder ?? null,
      comment: definition === value ? '' : value?.comment || '',
      assignedAt: definition === value ? null : value?.assignedAt ?? value?.createdAt ?? null,
      assignmentUpdatedAt: definition === value ? null : value?.updatedAt || null,
      expiresAt: definition === value ? null : value?.expiresAt || null,
    };
  }

  async getMarkAssignments(clubId, playerIds, { status = 'all' } = {}) {
    if (!playerIds.length) return { labels: [], categories: [] };
    const load = async (method) => {
      const rows = [];
      let page = 1;
      let pageCount = 1;
      do {
        const result = await this[method](clubId, { playerIds, status, page, pageSize: 100 });
        rows.push(...result.data);
        pageCount = result.meta?.pagination?.pageCount || 1;
        page++;
      } while (page <= pageCount);
      return rows;
    };
    const [labels, categories] = await Promise.all([load('listLabelAssignments'), load('listCategoryAssignments')]);
    return { labels, categories };
  }
  listLabelAssignments(clubId, options) { return this.listMarkAssignments('label', clubId, options); }
  listCategoryAssignments(clubId, options) { return this.listMarkAssignments('category', clubId, options); }
  assignLabel(playerId, labelId, data) { return this.saveMarkAssignment('label', null, data, playerId, labelId); }
  assignCategory(playerId, categoryId, data) { return this.saveMarkAssignment('category', null, data, playerId, categoryId); }
  updateLabelAssignment(id, data) { if (!id) throw new Error('assignmentId is required'); return this.saveMarkAssignment('label', id, data); }
  updateCategoryAssignment(id, data) { if (!id) throw new Error('assignmentId is required'); return this.saveMarkAssignment('category', id, data); }
  async removeLabelAssignment(id) {
    if (!id) throw new Error('assignmentId is required');
    await RootApiService.api.delete(`/player-labels/${id}`);
  }
  async removeCategoryAssignment(id) {
    if (!id) throw new Error('assignmentId is required');
    await RootApiService.api.delete(`/player-categories/${id}`);
  }

  // status: all | active | expired; отсутствие срока означает бессрочное назначение.
  async listMarkAssignments(kind, clubId, { playerId, playerIds, labelId, categoryId, status = 'all', page = 1, pageSize = 25 } = {}) {
    if (!clubId) throw new Error('clubId is required');
    if (!['all', 'active', 'expired'].includes(status)) throw new Error('Invalid assignment status');
    const resource = kind === 'label' ? 'player-labels' : 'player-categories';
    const definitionId = kind === 'label' ? labelId : categoryId;
    const filters = { [kind]: { club: { documentId: { $eq: clubId } } } };
    if (definitionId) filters[kind].documentId = { $eq: definitionId };
    if (playerId) filters.player = { documentId: { $eq: playerId } };
    else if (Array.isArray(playerIds)) {
      if (!playerIds.length) return { data: [], meta: { pagination: { page, pageSize, pageCount: 0, total: 0 } } };
      filters.player = { documentId: { $in: playerIds } };
    }
    const now = new Date().toISOString();
    if (status === 'active') filters.$or = [{ expiresAt: { $null: true } }, { expiresAt: { $gt: now } }];
    if (status === 'expired') filters.expiresAt = { $lte: now };
    const response = await RootApiService.api.get(`/${resource}`, {
      params: { filters, pagination: { page, pageSize }, sort: ['createdAt:desc'],
        populate: {
          player: { fields: ['documentId', 'name'] },
          [kind]: { populate: { image: true } },
        } },
    });
    return { data: (response.data?.data || []).map(entry => ({ ...entry, id: entry.documentId })), meta: response.data?.meta || {} };
  }

  async saveMarkAssignment(kind, id, values = {}, playerId, definitionId) {
    const resource = kind === 'label' ? 'player-labels' : 'player-categories';
    const data = Object.fromEntries(['assignedAt', 'expiresAt', 'comment']
      .filter(key => values[key] !== undefined).map(key => [key, values[key]]));
    if (!id) {
      if (!playerId || !definitionId) throw new Error('playerId and markId are required');
      data.player = playerId;
      data[kind] = definitionId;
    }
    const response = id
      ? await RootApiService.api.put(`/${resource}/${id}`, { data })
      : await RootApiService.api.post(`/${resource}`, { data });
    const entry = response.data?.data;
    return entry ? { ...entry, id: entry.documentId } : null;
  }
  #buildRatingsMap(entry) {
    const attrs = entry?.attributes || entry;
    const ratingsRaw = Array.isArray(attrs?.ratings?.data)
      ? attrs.ratings.data
      : Array.isArray(attrs?.ratings)
        ? attrs.ratings
        : [];

    return ratingsRaw.reduce((acc, ratingEntry) => {
      const rating = ratingEntry?.attributes || ratingEntry;
      const key = rating?.controlType;
      if (key) {
        acc[key] = {
          rating: rating.rating,
          games: rating.games,
          peak: rating.peak,
          kFactor: rating.kFactor,
          provisional: rating.provisional,
        };
      }
      return acc;
    }, {});
  }

  /**
   * Получает первых 20 игроков, исключая указанные идентификаторы и (опционально) фильтруя по клубу.
   * @param {Array<string>} excludeIds - Массив идентификаторов для исключения.
   * @param {string|null} clubId - Идентификатор клуба. Если null, фильтрация по клубу не применяется.
   * @param {string|null} ratingType - Тип рейтинга для фильтрации player.ratings.
   * @returns {Promise<Array<Player>>} Массив игроков.
   */
  async getFirstPlayers(excludeIds = [], clubId = null, ratingType = null) {
    try {
      const ratingsPopulate = {
        fields: ['type', 'controlType', 'rating', 'games', 'peak', 'kFactor', 'provisional']
      };
      if (ratingType) {
        ratingsPopulate.filters = {
          type: { $eq: ratingType }
        };
      }

      const params = {
        pagination: {
          page: 1,
          pageSize: 20
        },
        sort: ['updatedAt:desc'],
        filters: {},
        populate: {
          chess_clubs: {
            fields: ['documentId']
          },
          ratings: ratingsPopulate,
        }
      };

      if (excludeIds.length > 0) {
        params.filters['documentId'] = { $notIn: excludeIds };
      }

      if (clubId) {
        params.filters.chess_clubs = {
          documentId: { $eq: clubId }
        };
      }

      const response = await this.api.get('/', { params });

      const entries = response.data?.data || [];
      return entries.map((entry) => {
        const ratingsMap = this.#buildRatingsMap(entry);

        const normalizedEntry = { ...entry };
        if (Object.keys(ratingsMap).length > 0) {
          normalizedEntry.ratingsMap = ratingsMap;
        }
        return this.deserialize(normalizedEntry);
      });
    } catch (e) {
      log.error('getFirstPlayers', 'Ошибка при получении первых игроков', e);
      return [];
    }
  }

  /**
   * Ищет игроков по подстроке имени (регистр не учитывается).
   * @param {string} query - Строка для поиска по имени.
   * @param {string|null} clubId - Идентификатор клуба. Если null, фильтрация по клубу не применяется.
   * @param {string|null} ratingType - Тип рейтинга для фильтрации player.ratings.
   * @returns {Promise<Array<Player>>} Массив найденных игроков.
   */
  async search(query, clubId = null, ratingType = null, profile = null, scope = 'all', mode = 'suggestions') {
    if (!clubId) throw new Error('Club is required for player registration search');
    const response = await RootApiService.api.get(`/clubs/${clubId}/player-search`, {
      params: { q: query, scope, ...(mode === 'confirmation' ? { mode } : {}), ...(ratingType ? { ratingType } : {}),
        ...(profile ? { similar: true, firstName: profile.firstName, lastName: profile.lastName } : {}) },
    });
    return this.asArray(response.data?.data).map(entry => {
      const player = this.deserialize(entry);
      player.membershipExternal = entry.membershipExternal;
      player.clubNames = (entry.chess_clubs || []).map(club => club.name).filter(Boolean);
      return player;
    });
  }

  /**
   * Ищет игроков по массиву имён (точное соответствие по БД), опционально ограничивая по клубу.
   * Важно: соответствие по $in может быть регистрозависимым в зависимости от коллации БД.
   * @param {string[]} names - Список имён для поиска.
   * @param {string|null} clubId - Если указан, ищет только среди игроков клуба.
   * @returns {Promise<Array<Player>>} Массив найденных игроков.
   */
  async searchByNames(names = [], clubId = null) {
    try {
      const list = (Array.isArray(names) ? names : [])
        .map(n => (typeof n === 'string' ? n.trim() : ''))
        .filter(Boolean);

      if (list.length === 0) return [];

      const filters = {
        name: { $in: list }
      };

      if (clubId) {
        filters.chess_clubs = {
          documentId: { $eq: clubId }
        };
      }

      const response = await this.api.get('/', {
        params: {
          filters,
          pagination: {
            page: 1,
            pageSize: Math.min(Math.max(list.length, 20), 1000)
          },
          populate: {
            chess_clubs: {
              fields: ['documentId']
            }
          }
        }
      });
      
      const entries = response.data?.data || [];
      const players = entries.map((entry) => {
        return this.deserialize(entry);
      });
      log('searchByNames', 'Ответ', response);
      log('searchByNames', 'Игроки', players);

      if (players.length < list.length) {
        const found = new Set(players.map(p => p.name));
        const missing = list.filter(n => !found.has(n));
        if (missing.length) {
          log.warn('searchByNames', 'Не все имена найдены по точному совпадению', { missing });
        }
      }

      return players;
    } catch (e) {
      log.error('searchByNames', 'Ошибка при поиске игроков по именам', e);
      return [];
    }
  }

  /**
   * Обновляет данные игрока. Если указан tournamentId, добавляет игрока к турниру, если он ещё не добавлен.
   * @param {Player} player - Объект игрока для обновления.
   * @returns {Promise<Player|null>} Обновлённый игрок или null, если не найден.
   */
  async update(player) {
    let result;
    const id = player.id;

    log('update', 'model:', player);

    if (!player.tournamentId) {
      result = await super.update(player);
      result.clubId = await this.getChessClubsForPlayer(result);
    } else {
      try {
        const res = await this.api.get(`/${id}`, {
          params: {
            populate: {
              tournaments: {
                fields: ['documentId']
              }
            }
          }
        });

        const entry = res.data?.data;

        if (!entry) {
          log.warn('update', `Игрок с id ${id} не найден`);
          return null;
        }

        const tournamentIds = Array.isArray(entry.tournaments)
          ? entry.tournaments.map(t => t.documentId)
          : [];

        if (tournamentIds.includes(player.tournamentId)) {
          log('update', 'Игрок уже участвует в этом турнире');
          result = await super.update(player);
          result.clubId = await this.getChessClubsForPlayer(result);
        } else {
          log('update', 'Добавим игрока к турниру');
          tournamentIds.push(player.tournamentId);
          player.tournaments = tournamentIds;
          delete player.tournamentId;
          result = await super.update(player);
          result.clubId = await this.getChessClubsForPlayer(result);
        }

        return result;
      } catch (e) {
        if (e.response?.status === 404) {
          log.warn('update', `Игрок с id ${id} не найден (404)`);
          return null;
        }
        log.error('update', `Ошибка при получении игрока ${id}`, e);
        return null;
      }
    }

    return result;
  }

  /**
     * Создаёт нового игрока и добавляет его к турниру.
     * @param {Player} player - Новый игрок.
     * @returns {Promise<Player>} Созданный игрок.
     */
  async create(player) {
    try {
      log('create', 'Создается', player);

      if (player.tournamentId) {
        player.tournaments = [player.tournamentId];
        delete player.tournamentId;
      }
      const result = await super.create(player);
      result.clubId = await this.getChessClubsForPlayer(result);
      return result;
    } catch (e) {
      log.error('create', 'Ошибка при создании игрока', e);
      throw e; // или можно вернуть null, если ты хочешь "мягкое падение"
    }
  }

    /**
   * Получает массив documentId клубов, связанных с игроком.
   * @param {Player} player - Игрок для запроса.
   * @returns {Promise<string[]>} Список documentId клубов.
   */
  async getChessClubsForPlayer(player) {
    try {
      const res = await this.api.get('/', {
        params: {
          filters: {
            documentId: { $eq: player.id }
          },
          populate: {
            chess_clubs: {
              fields: ['documentId']
            }
          }
        }
      });
      const entry = res.data?.data?.[0];
      const clubsRaw = Array.isArray(entry?.chess_clubs?.data)
        ? entry.chess_clubs.data
        : Array.isArray(entry?.chess_clubs)
          ? entry.chess_clubs
          : [];
      const clubIds = clubsRaw.map(c => c.documentId).filter(Boolean);
      log('getChessClubsForPlayer', 'clubIds', clubIds);
      return clubIds;
    } catch (e) {
      log.error('getChessClubsForPlayer', 'Ошибка при получении клубов игрока', e);
      return [];
    }
  }

  /**
   * Получает всех игроков клуба, исключая удалённые.
   * @param {string} clubId - Идентификатор шахматного клуба.
   * @param {Object} [options={}] - Дополнительные параметры.
   * @param {boolean} [options.ratedOnly=false] - Учитывать только рейтинговые матчи и турниры.
   * @param {string|null} [options.ratingType=null] - Тип рейтинга для фильтрации player.ratings.
   * @returns {Promise<Player[]>} Список игроков.
   */
  async getAllByClub(clubId, options = {}) {
    log('getAllByClub', 'request', clubId);
    const { ratedOnly = false, ratingType = null } = options;
    try {
      const ratingsPopulate = {
        fields: ['type', 'controlType', 'rating', 'games', 'peak', 'kFactor', 'provisional']
      };
      if (ratingType) {
        ratingsPopulate.filters = {
          type: { $eq: ratingType }
        };
      }

      const entries = [];
      let page = 1;
      let pageCount = 1;
      do {
        const res = await this.api.get('/', {
          params: {
          filters: {
            deleted: { $ne: true },
            chess_clubs: { documentId: { $eq: clubId } }
          },
          fields: ['name', 'firstName', 'lastName', 'createdAt', 'updatedAt'],
          populate: {
            chess_clubs: {
              fields: ['documentId']
            },
            fideTitle: {
              fields: ['code', 'name', 'scope', 'priority']
            },
            localTitles: {
              fields: ['code', 'name', 'scope', 'priority'],
              filters: { chess_club: { documentId: { $eq: clubId } } }
            },
            ratings: ratingsPopulate,
            gamesAsWhiteSide: {
              count: true,
              filters: { deleted: { $ne: true }, tournament: { deleted: { $ne: true } }, ...(ratedOnly ? { rated: { $eq: true } } : {}) }
            },
            gamesAsBlackSide: {
              count: true,
              filters: { deleted: { $ne: true }, tournament: { deleted: { $ne: true } }, ...(ratedOnly ? { rated: { $eq: true } } : {}) }
            },
            tournaments: {
              count: true,
              filters: ratedOnly
                ? { deleted: { $ne: true }, rated: { $eq: true } }
                : { deleted: { $ne: true } }
            }
          },
          pagination: {
            page,
            pageSize: 100
          }
          },
          skipToken: true
        });
        entries.push(...this.asArray(res.data?.data));
        pageCount = res.data?.meta?.pagination?.pageCount || 1;
        page += 1;
      } while (page <= pageCount);

      log('getAllByClub', '[club] entries', entries);

      return entries
        .map((entry, index) => {
          try {
            log('getAllByClub', `Обработка entry[${index}]`, entry);

            const attrs = entry?.attributes || entry;
            const gamesCount = (attrs?.gamesAsWhiteSide?.count ?? 0) + (attrs?.gamesAsBlackSide?.count ?? 0);
            const tournamentsCount = attrs?.tournaments?.count ?? 0;
            const ratingsRaw = Array.isArray(attrs?.ratings?.data)
              ? attrs.ratings.data
              : Array.isArray(attrs?.ratings)
                ? attrs.ratings
                : [];
            const ratingsMap = ratingsRaw.reduce((acc, r) => {
              const rr = r.attributes || r;
              const key = rr.controlType;
              if (key) {
                acc[key] = {
                  rating: rr.rating,
                  games: rr.games,
                  peak: rr.peak,
                  kFactor: rr.kFactor,
                  provisional: rr.provisional
                };
              }
              return acc;
            }, {});

            const unwrap = value => value?.attributes ? { ...value.attributes, documentId: value.documentId } : value;
            const fideTitle = unwrap(attrs?.fideTitle?.data ?? attrs?.fideTitle);
            const localTitles = this.asArray(attrs?.localTitles?.data ?? attrs?.localTitles)
              .map(unwrap)
              .map(title => ({ ...title, chess_club: clubId }));
            const title = [fideTitle, ...localTitles].filter(Boolean);
            const normalizedEntry = entry?.attributes
              ? { ...entry, attributes: { ...entry.attributes, title } }
              : { ...entry, title };
            const obj = this.deserialize(normalizedEntry);
            if (Object.keys(ratingsMap).length > 0) {
              obj.ratingsMap = ratingsMap;
            }
            log('getAllByClub', `Результат deserialize[${index}]`, obj);
            return {
              player: obj,
              gamesCount,
              tournamentsCount,
              ratings: ratingsRaw.map((ratingEntry) => {
                const rating = ratingEntry?.attributes || ratingEntry;
                return {
                  documentId: ratingEntry?.documentId || rating?.documentId || null,
                  key: rating?.type && rating?.controlType ? `${rating.type}:${rating.controlType}` : null,
                  type: rating?.type || null,
                  controlType: rating?.controlType || null,
                  rating: rating?.rating,
                  games: rating?.games,
                  peak: rating?.peak,
                  kFactor: rating?.kFactor,
                  provisional: rating?.provisional,
                };
              }),
            };
          } catch (e) {
            log.error('getAllByClub', `Ошибка при обработке entry[${index}]`, e.message, entry);
            return null;
          }
        })
        .filter(item => item !== null);

    } catch (e) {
      log.error('getAllByClub', `Ошибка при получении всех ${this.resourcePath}`, e);
      return [];
    }
  }

  async getAssignableTitles(clubId) {
    if (!clubId) throw new Error('clubId is required');
    const titles = [];
    let page = 1;
    let pageCount = 1;
    do {
      const response = await RootApiService.api.get('/titles', { params: {
        filters: { isActive: { $eq: true }, $or: [
          { scope: { $eq: 'fide' } },
          { scope: { $eq: 'local' }, chess_club: { documentId: { $eq: clubId } } },
        ] },
        fields: ['code', 'name', 'scope'], sort: ['priority:asc', 'code:asc'],
        pagination: { page, pageSize: 100 },
      } });
      titles.push(...this.asArray(response.data?.data));
      pageCount = response.data?.meta?.pagination?.pageCount || 1;
      page += 1;
    } while (page <= pageCount);
    return titles;
  }

  async removeTitle(playerId, title) {
    await this.api.put(`/${playerId}`, { data: this.#titleUpdate(playerId, null, title) });
  }

  #titleUpdate(playerId, title, currentTitle = null) {
    const target = title || currentTitle;
    if (!playerId || !target?.documentId || !['local', 'fide'].includes(target.scope)) throw new Error('Invalid title');
    if (!title) return currentTitle.scope === 'fide'
      ? { fideTitle: null }
      : { localTitles: { disconnect: [currentTitle.documentId] } };
    const data = title.scope === 'fide'
      ? { fideTitle: title.documentId }
      : { localTitles: { connect: [title.documentId] } };
    if (title.scope === 'local' && currentTitle?.scope === 'local' && currentTitle.documentId !== title.documentId) {
      data.localTitles.disconnect = [currentTitle.documentId];
    }
    return data;
  }

  async assignTitle(playerId, title, currentTitle = null) {
    if (!title) throw new Error('Invalid title');
    await this.api.put(`/${playerId}`, { data: this.#titleUpdate(playerId, title, currentTitle) });
  }

  async isDisplayNameAvailable(name, playerId, clubId) {
    if (!clubId) throw new Error('clubId is required');
    const response = await this.api.get('/', { params: {
      filters: {
        name: { $eqi: normalizePlayerName(name) },
        documentId: { $ne: playerId },
        chess_clubs: { documentId: { $eq: clubId } },
      },
      fields: ['documentId'], pagination: { page: 1, pageSize: 1 },
    } });
    return this.asArray(response.data?.data).length === 0;
  }

  async createPlayerDetails(clubId, details, titles = []) {
    if (!clubId || !details.name?.trim()) throw new Error('Club and player name are required');
    const data = Object.fromEntries(['name', 'firstName', 'lastName'].map(field => {
      const value = normalizePlayerName(details[field]);
      const error = validatePlayerName(value, field === 'name');
      if (error) throw new Error(error);
      return [field, value];
    }));
    data.chess_clubs = [clubId];
    const fideTitleId = titles.find(item => item.scope === 'fide')?.documentId;
    if (fideTitleId) data.fideTitle = fideTitleId;
    data.localTitles = titles.filter(item => item.scope === 'local').map(item => item.documentId);
    const response = await this.api.post('/', { data });
    return response.data?.data || null;
  }

  async updatePlayerDetails(playerId, details, { title, currentTitle = null, removeTitle = false, titles, originalTitles = [], sharedProfile = false } = {}) {
    if (!playerId || !details.name?.trim()) throw new Error('Player name is required');
    const data = Object.fromEntries((sharedProfile ? [] : ['name', 'firstName', 'lastName']).map(field => {
      const value = normalizePlayerName(details[field]);
      const error = validatePlayerName(value, field === 'name');
      if (error) throw new Error(error);
      return [field, value];
    }));
    if (removeTitle || title) Object.assign(data, this.#titleUpdate(playerId, removeTitle ? null : title, currentTitle));
    if (titles) {
      const beforeFide = originalTitles.find(item => item.scope === 'fide')?.documentId || null;
      const afterFide = titles.find(item => item.scope === 'fide')?.documentId || null;
      if (beforeFide !== afterFide) data.fideTitle = afterFide;
      const beforeLocal = originalTitles.filter(item => item.scope === 'local').map(item => item.documentId);
      const afterLocal = titles.filter(item => item.scope === 'local').map(item => item.documentId);
      const connect = afterLocal.filter(id => !beforeLocal.includes(id));
      const disconnect = beforeLocal.filter(id => !afterLocal.includes(id));
      if (connect.length || disconnect.length) data.localTitles = { connect, disconnect };
    }
    await this.api.put(`/${playerId}`, { data });
  }

  async getClubPlayersForManagement(clubId, { page = 1, pageSize = 25, search = '', title = '', labelId = '', categoryId = '', payment = '', ratingType = '', controlType = '', ratingFrom = '', ratingTo = '', sortField = 'createdAt', sortDirection = 'desc', playerIds = null } = {}) {
    if (!clubId) throw new Error('clubId is required');
    if (Array.isArray(playerIds) && !playerIds.length) return { data: [], pagination: { page, pageSize, total: 0, pageCount: 0 } };
    const direction = sortDirection === 'desc' ? 'desc' : 'asc';
    const field = ['name', 'firstName', 'lastName', 'createdAt', 'updatedAt'].includes(sortField) ? sortField : 'name';
    const filters = { deleted: { $ne: true }, chess_clubs: { documentId: { $eq: clubId } } };
    const query = search.trim();
    if (title === 'fide') filters.fideTitle = { documentId: { $notNull: true } };
    if (title === 'local') filters.localTitles = { chess_club: { documentId: { $eq: clubId } } };
    const anyTitle = { $or: [
      { fideTitle: { documentId: { $notNull: true } } },
      { localTitles: { chess_club: { documentId: { $eq: clubId } } } },
    ] };
    if (title === 'any') filters.$and = [anyTitle];
    if (title === 'none') {
      // Negating a joined localTitles row does not exclude a player with titles in several clubs.
      const titledIds = [];
      let titlePage = 1;
      let titlePageCount = 1;
      do {
        const titled = await this.api.get('/', { params: {
          filters: { ...filters, $and: [anyTitle] },
          fields: ['documentId'],
          sort: ['documentId:asc'],
          pagination: { page: titlePage, pageSize: 100 },
        } });
        titledIds.push(...this.asArray(titled.data?.data).map(player => player.documentId));
        titlePageCount = titled.data?.meta?.pagination?.pageCount || 1;
        titlePage += 1;
      } while (titlePage <= titlePageCount);
      if (titledIds.length) filters.documentId = { $notIn: titledIds };
    }
    if (Array.isArray(playerIds)) filters.documentId = { ...filters.documentId, $in: playerIds };
    if (ratingType || controlType || ratingFrom !== '' || ratingTo !== '') {
      filters.ratings = { documentId: { $notNull: true } };
      if (ratingType) filters.ratings.type = { $eq: ratingType };
      if (controlType) filters.ratings.controlType = { $eq: controlType };
      if (ratingFrom !== '' || ratingTo !== '') {
        filters.ratings.rating = {};
        if (ratingFrom !== '') filters.ratings.rating.$gte = Number(ratingFrom);
        if (ratingTo !== '') filters.ratings.rating.$lte = Number(ratingTo);
      }
    }
    const response = await this.api.get('/', { params: {
      filters,
      ...(query ? { playerSearch: query } : {}),
      fields: ['name', 'firstName', 'lastName', 'createdAt', 'updatedAt'],
      ...(labelId ? { labelId } : {}),
      ...(categoryId ? { categoryId } : {}),
      paymentInfo: 'true',
      ...(payment ? { paymentFilter: payment } : {}),
      populate: {
        fideTitle: { fields: ['code', 'name', 'scope', 'priority'] },
        localTitles: { fields: ['code', 'name', 'scope', 'priority'], filters: { chess_club: { documentId: { $eq: clubId } } } },
        ratings: { fields: ['type', 'controlType', 'rating', 'games', 'peak', 'kFactor', 'provisional'] },
      },
      ...(sortField === 'titles'
        ? { titleSort: direction }
        : ['labels', 'categories'].includes(sortField)
          ? { markSort: sortField === 'labels' ? 'label' : 'category', markSortDirection: direction }
          : sortField === 'payment'
            ? { paymentSort: 'payment', paymentSortDirection: direction }
          : { sort: [`${field}:${direction}`, 'documentId:asc'] }),
      pagination: { page, pageSize },
    } });
    const unwrap = entry => entry?.attributes ? { ...entry.attributes, documentId: entry.documentId } : entry;
    return {
      data: this.asArray(response.data?.data).map(entry => {
        const player = unwrap(entry);
        return { ...player,
          player: this.deserialize(player),
          fideTitle: unwrap(player.fideTitle?.data ?? player.fideTitle),
          localTitles: this.asArray(player.localTitles?.data ?? player.localTitles).map(unwrap),
          ratings: this.asArray(player.ratings?.data ?? player.ratings).map(unwrap),
          payment: player.payment || { feeExempt: false, subscription: null },
        };
      }),
      pagination: response.data?.meta?.pagination,
    };
  }

  /**
   * Получает статистику игроков клуба через кастомный эндпоинт Strapi.
   * Эндпоинт: GET /api/players/club-stats
   *
   * @param {string} clubId - documentId клуба (обязателен)
   * @param {Object} [opts]
   * @param {number} [opts.page=1]
   * @param {number} [opts.pageSize=20]
   * @param {string} [opts.search='']
   * @param {string|null} [opts.chessVariant=null]
   * @param {string|null} [opts.ratingType=null]
   * @param {string|null} [opts.controlType=null]
   * @returns {Promise<{data:Array<{player:Player, ratingsMap:Object, stats:Object}>, meta:Object}>}
   */
  async getClubStats(clubId, opts = {}) {
    const { page = 1, pageSize = 20, search = '', chessVariant = null, ratingType = null, controlType = null, sortField = null, sortDirection = null } = opts || {};

    if (!clubId) {
      throw new Error('[getClubStats] clubId is required');
    }

    try {
      const normalizedSearch = typeof search === 'string' ? search.trim() : '';
      const params = {
        clubId,
        pagination: {
          page: Math.max(1, page),
          pageSize: Math.min(Math.max(pageSize, 1), 1000)
        }
      };

      if (normalizedSearch) {
        params.search = normalizedSearch;
      }
      if (chessVariant) {
        params.chessVariant = chessVariant;
      }
      if (ratingType) {
        params.ratingType = ratingType;
      }
      if (controlType) {
        params.controlType = controlType;
      }

      log('getClubStats', 'request', params);
      if (sortField) params.sortField = sortField;
      if (sortDirection) params.sortDirection = sortDirection;

      // this.api уже настроен на ресурс /players
      const res = await this.api.get('/club-stats', { params, skipToken: true });

      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      const meta = res.data?.meta || {};

      const mapped = rows
        .map((row) => {
          try {
            const playerModel = row?.player ? this.deserialize(row.player) : null;
            if (!playerModel) return null;

            if (row.ratingsMap) {
              playerModel.ratingsMap = row.ratingsMap;
            }

            return {
              player: playerModel,
              ratingsMap: row.ratingsMap || {},
              stats: row.stats || {},
            };
          } catch (e) {
            log.error('getClubStats', 'Ошибка при обработке строки', e, row);
            return null;
          }
        })
        .filter(Boolean);

      return { data: mapped, meta };
    } catch (e) {
      log.error('getClubStats', 'Ошибка при получении статистики игроков клуба', e);
      if (opts.throwOnError) throw e;
      return { data: [], meta: { pagination: { page: 1, pageSize: 0, pageCount: 0, total: 0 } } };
    }
  }

  /**
   * Получает актуальные рейтинги игроков клуба.
   * playerIds и clubId — обязательны.
   *
   * @param {string[]} playerIds - documentId игроков
   * @param {string} clubId - documentId клуба
   * @param {Object} [opts]
   * @param {string|string[]|null} [opts.ratingType=null] - тип рейтинга или массив типов
   * @param {string|string[]|null} [opts.controlType=null] - тип контроля или массив
   * @param {number} [opts.page=1]
   * @param {number} [opts.pageSize=1000]
   * @returns {Promise<Object>}
   */
  async getCurrentRatings(playerIds, clubId, opts = {}) {
    const {
      ratingType = null,
      controlType = null,
      page = 1,
      pageSize = 1000,
    } = opts || {};

    if (!Array.isArray(playerIds) || playerIds.length === 0) {
      throw new Error('[getCurrentRatings] playerIds is required and must be non-empty');
    }
    if (!clubId) {
      throw new Error('[getCurrentRatings] clubId is required');
    }

    try {
      const filters = {
        player: {
          documentId: { $in: playerIds },
          chess_clubs: { documentId: { $eq: clubId } },
        },
      };

      if (ratingType) {
        filters.type = Array.isArray(ratingType)
          ? { $in: ratingType }
          : { $eq: ratingType };
      }

      if (controlType) {
        filters.controlType = Array.isArray(controlType)
          ? { $in: controlType }
          : { $eq: controlType };
      }

      const params = {
        filters,
        sort: ['rating:desc'],
        pagination: {
          page: Math.max(1, page),
          pageSize: Math.min(Math.max(pageSize, 1), 10000),
        },
        populate: {
          player: {
            fields: ['documentId', 'name'],
          },
        },
      };

      log('getCurrentRatings', 'request', {
        playerIds,
        clubId,
        ratingType,
        controlType,
        page,
        pageSize,
      });

      const res = await RootApiService.api.get('/ratings', {
        params,
        skipToken: true,
      });

      const entries = this.asArray(res.data?.data);

      const rows = entries
        .map((entry) => {
          const attrs = entry?.attributes || entry;
          const playerEntry = attrs?.player?.data || attrs?.player;
          const playerAttrs = playerEntry?.attributes || playerEntry;

          return {
            playerId: playerEntry?.documentId,
            playerName: playerAttrs?.name,
            controlType: attrs?.controlType,
            type: attrs?.type,
            rating: attrs?.rating,
            games: attrs?.games,
            peak: attrs?.peak,
            kFactor: attrs?.kFactor,
            provisional: attrs?.provisional,
          };
        })
        .filter((r) => r.playerId && r.type && r.controlType);

      // Return shape: { [playerId]: { [ratingType]: { [controlType]: { ... } } } }
      const resultMap = rows.reduce((acc, r) => {
        acc[r.playerId] ||= {};
        acc[r.playerId][r.type] ||= {};
        acc[r.playerId][r.type][r.controlType] = {
          playerName: r.playerName,
          rating: r.rating,
          games: r.games,
          peak: r.peak,
          kFactor: r.kFactor,
          provisional: r.provisional,
        };
        return acc;
      }, {});

      log('getCurrentRatings', 'result count', rows.length);
      return resultMap;
    } catch (e) {
      log.error('getCurrentRatings', 'Ошибка при получении рейтингов', e);
      return {};
    }
  }

  /**
   * Удаляет все записи рейтингов (ratings) для конкретного игрока.
   * Шаги: GET (before) по player.documentId → DELETE /ratings/:documentId → GET (after) и проверка.
   * @param {string} playerId documentId игрока
   * @param {string} [ratingType='elo_t'] тип рейтинга
   * @returns {Promise<{playerId:string,type:string,removed:number,message:string}>}
   */
  async clearPlayerRatings(playerId, ratingType = 'elo_t') {
    if (!playerId) throw new Error('[clearPlayerRatings] playerId is required');
    try {
      const params = {
        filters: {
          player: { documentId: { $eq: playerId } },
          type: { $eq: ratingType },
        },
        fields: ['documentId'],
        pagination: { pageSize: 10000 },
      };

      log('clearPlayerRatings', '▶️ GET(before)', '/ratings', params);
      const listResBefore = await RootApiService.api.get('/ratings', { params });
      const entriesBefore = this.asArray(listResBefore?.data?.data);
      const docIds = entriesBefore.map(e => e?.documentId).filter(Boolean);
      const beforeCount = docIds.length;
      log('clearPlayerRatings', 'найдено (before):', beforeCount);

      // Early return if nothing to delete
      if (beforeCount === 0) {
        const result = {
          playerId,
          type: ratingType,
          removed: 0,
          message: translate('serviceMessages.playerRatingsRemoved', { count: 0 })
        };
        log('clearPlayerRatings', 'skip after-check: nothing to delete', result);
        return result;
      }

      let removed = 0;
      for (const docId of docIds) {
        try {
          log('clearPlayerRatings', '▶️ DELETE', `/ratings/${docId}`);
          await RootApiService.api.delete(`/ratings/${docId}`);
          removed += 1;
        } catch (e) {
          log.warn('clearPlayerRatings', '⚠️ не удалось удалить documentId=', docId, e);
        }
      }

      if (beforeCount > 0) {
        log('clearPlayerRatings', '▶️ GET(after)', '/ratings', params);
        const listResAfter = await RootApiService.api.get('/ratings', { params });
        const entriesAfter = this.asArray(listResAfter?.data?.data);
        const afterCount = entriesAfter.length;
        log('clearPlayerRatings', 'осталось (after):', afterCount);

        if (afterCount > 0) {
          throw new Error(translate('serviceMessages.playerRatingsRemoveIncomplete', {
            remaining: afterCount,
            total: beforeCount,
          }));
        }
      }

      const result = {
        playerId,
        type: ratingType,
        removed: beforeCount,
        message: translate('serviceMessages.playerRatingsRemoved', { count: beforeCount }),
      };
      log('clearPlayerRatings', '✅ done', result);
      return result;
    } catch (e) {
      log.error('clearPlayerRatings', '❌', e);
      throw e;
    }
  }

  /**
   * Десериализует объект игрока, полученный от API, в модель Player.
   * @param {Object} entry - Входящий объект от API.
   * @returns {Player} Экземпляр Player.
   * @throws {Error} Если данные некорректны.
   */
  deserialize(entry) {
    log('deserialize', 'entry', entry);

    const data = entry.attributes || entry;

    if (!entry || !entry.documentId || !data) {
      log.error('deserialize', 'Ошибка при десериализации', entry);
      throw new Error(translate('serviceMessages.invalidPlayerResponse'));
    }

    const { name, firstName, lastName, createdAt, updatedAt, chess_clubs, title, labels, categories } = data;
    const clubsRaw = Array.isArray(chess_clubs?.data)
      ? chess_clubs.data
      : Array.isArray(chess_clubs)
        ? chess_clubs
        : [];

    const clubIds = clubsRaw.map(c => c.documentId).filter(Boolean);

    if (!name) {
      throw new Error(translate('serviceMessages.incompletePlayerData', {
        id: entry.documentId,
        name,
      }));
    }

    const result = {
      id: entry.documentId,
      name: name,
      firstName,
      lastName,
      createdAt,
      updatedAt,
      clubId: clubIds,
      title: title ?? null,
      labels: Array.isArray(labels) ? labels : [],
      categories: Array.isArray(categories) ? categories : [],
    };

    const ratingsMap = entry.ratingsMap || this.#buildRatingsMap(entry);
    if (ratingsMap && Object.keys(ratingsMap).length > 0) {
      result.ratingsMap = ratingsMap;
    }

    log('deserialize', 'result', result);
    const player = Player.fromObject(result);
    return player;
  }

  /**
   * Сериализует экземпляр Player в формат для API.
   * @param {Player} player - Игрок для сериализации.
   * @returns {Object} Данные для отправки на сервер.
   */
  serialize(player) {
    log('serialize', 'Сериализуем игрока', player);
    const data = {
      name: player.name
    };

    if (player.firstName !== undefined) data.firstName = player.firstName;
    if (player.lastName !== undefined) data.lastName = player.lastName;

    if (Array.isArray(player.tournaments)) {
      data.tournaments = player.tournaments;
    }

    if (Array.isArray(player.clubId)) {
      data.chess_clubs = player.clubId;
    }

    return data;
  }
}

export default new PlayerApiService('players', Player);
